import { Component } from 'react'
import PropTypes from 'prop-types'
import { reduxForm } from 'redux-form'
import { first } from 'lodash'

import { Checkbox, Form, Input, SecretInput } from 'components/forms'
import Link from 'components/link'
import MailcheckedInput from 'components/mailchecked-input'
import { InfoButton } from 'components/buttons'
import { privacyPolicyPath, termsOfUsePath } from 'lib/urls'
import splitName from 'lib/split-name'
import {
  createValidator,
  email,
  fullName,
  minLength,
  required,
} from 'lib/form-validation'
import DueDateSelect from 'components/due-date-select'
import arrivalDate from '../../ArrivalDate/arrival-date'

const validator = createValidator({
  name: [required, fullName],
  email: [required, email],
  password: [required, minLength(8)],
})

export class OneStepSignupForm extends Component {
  static propTypes = {
    error: PropTypes.object,
    onSubmitSuccess: PropTypes.func.isRequired,
    fields: PropTypes.shape({
      name: PropTypes.object.isRequired,
      email: PropTypes.object.isRequired,
      password: PropTypes.object.isRequired,
      consentedToPromotionalOffers: PropTypes.object.isRequired,
    }),
    signedUp: PropTypes.bool,
    submitting: PropTypes.bool,
  }

  static defaultProps = {
    signedUp: false,
  }

  componentDidUpdate(prevProps) {
    // There is render bug which is reseting the values set by initialValues in redux-form.
    // This is a patch to re-initialize the form with the correct initialValues.
    // TODO: Refactor this so we're not using redux-form.
    if (
      this.props.fields.email.value !== prevProps.fields.email.value &&
      !this.props.fields.email.dirty
    ) {
      if (
        prevProps.fields.email.value &&
        prevProps.fields.email.value !== '' &&
        this.props.fields.email.value === ''
      ) {
        this.props.initializeForm({ email: prevProps.fields.email.value })
      }
    }
  }

  render() {
    const {
      children,
      signedUp,
      error,
      submitting,
      fields: { name, email, password, consentedToPromotionalOffers },
    } = this.props

    const submitButtonText = 'Create My Registry'
    return (
      <Form error={error} onSubmit={this.props.handleSubmit}>
        <Input
          {...name}
          disabled={signedUp}
          error={name.touched ? name.error : null}
          id="name-field"
          label="First Name and Last Name"
          name="name"
          type="text"
        />
        <DueDateSelect
          {...arrivalDate}
          error={arrivalDate.touched ? arrivalDate.error : null}
          groupClassName="mbm"
          label="Arrival Date (Optional)"
          name="arrivalDate"
        />
        <MailcheckedInput
          {...email}
          mailcheckOnChange
          disabled={signedUp}
          error={email.touched ? email.error : null}
          id="email-field"
          label="Email address"
          mailcheckDelay={300}
          mailcheckOnBlur={false}
          name="email"
          type="text"
        />
        <SecretInput
          {...password}
          autoCapitalize="none"
          autoComplete="new-password"
          autoCorrect="off"
          disabled={signedUp}
          error={password.touched ? password.error : null}
          help="Passwords must be at least 8 characters"
          id="password-field"
          label="Password"
        />
        {!signedUp && (
          <>
            <Checkbox
              {...consentedToPromotionalOffers}
              checked={!!consentedToPromotionalOffers.value}
              defaultChecked={false}
              label="I would like to receive promotional offers and updates from approved Babylist partners."
            />
            <>
              {children}
              <div className="btn-group btn-group--stretch">
                <InfoButton
                  // DO NOT REMOVE: Allows 3rd party tracking pixels to identify when this button is clicked
                  block
                  pill
                  className="mbl"
                  data-external-tracking-identifier="signup"
                  disabled={signedUp || submitting}
                  submitting={submitting}
                  type="submit"
                >
                  {submitButtonText}
                </InfoButton>
              </div>
              <p className="h6 text-center text-muted">
                By clicking {submitButtonText} you agree to the
                <br className="visible-xs" /> Babylist{' '}
                <Link newWindow url={termsOfUsePath}>
                  terms of use
                </Link>{' '}
                and{' '}
                <Link newWindow url={privacyPolicyPath}>
                  privacy policy
                </Link>
                .
              </p>
            </>
          </>
        )}
      </Form>
    )
  }
}

// Should never really get here, but fallback if client validation fails
const getNameError = (firstNameError, lastNameError) => {
  if (firstNameError && firstNameError == "can't be blank") {
    return 'Add your first and last name'
  }
  if (lastNameError && lastNameError == "can't be blank") {
    return 'Add your last name'
  }
  if (firstNameError) {
    return `First name ${firstNameError}`
  }
  if (lastNameError) {
    return `Last name ${lastNameError}`
  }
  return null
}

export const mapStateToProps = (state, ownProps) => ({
  // Shimmed hack into redux-form
  // This is necessary because we need to format name data right before submitting.
  // ReduxForm automatically will call onSubmit in this.props.handleSubmit
  onSubmit: ({ name, ...data }) =>
    ownProps
      .onSubmit({
        source: ownProps.signupSource,
        ...splitName(name),
        ...data,
      })
      .catch((resp) => {
        const user = resp.user || {}
        return Promise.reject({
          _error: resp._error,
          // taking advantage of union's nice handling of undefineds
          name: getNameError(first(user.firstName), first(user.lastName)),
          email: user.email,
          password: user.password,
        })
      }),
})

export default reduxForm(
  {
    form: 'signupForm',
    fields: ['name', 'email', 'password', 'consentedToPromotionalOffers'],
    validate: validator,
  },
  mapStateToProps
)(OneStepSignupForm)
